import * as THREE from 'three';
import type { Terrain } from '../game/Terrain';

/**
 * Click-and-hold bug-spray hazard. While the `B` key is held and the cursor is
 * over terrain, a flat fan of "mist" appears at the cursor and damages +
 * scatters any ants standing in its cone footprint.
 *
 * The spray direction is the camera's XZ-projected gaze: we take the vector
 * from the camera's XZ position to the cursor hit, which points roughly "away"
 * from the viewer into the world.
 */
export class BugSpray {
  readonly group: THREE.Group;

  /** Cone length (from tip near cursor → far edge along spray direction). */
  private readonly length = 2.5;
  /** Cone width at the far end. */
  private readonly farWidth = 1.2;
  /** Fan subdivisions. */
  private readonly segments = 12;

  /** Damage area approximation — a 1m circle at the cone's mid-point. */
  private readonly damageRadius = 1.0;
  /** Damage per second while held. */
  private readonly dps = 1.5;
  /** Scatter impulse radius. */
  private readonly scatterRadius = 2.0;
  /** Scatter impulse strength. */
  private readonly scatterForce = 0.5;

  private readonly terrain: Terrain;

  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly geometry: THREE.BufferGeometry;

  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly hitPoint = new THREE.Vector3();
  /** Direction of the spray on the XZ plane (unit). */
  private readonly dir = new THREE.Vector2();

  private keyHeld = false;
  private hasHit = false;
  private hasCursor = false;

  private dom: HTMLCanvasElement | null = null;
  private camera: THREE.PerspectiveCamera | null = null;

  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'b' || e.key === 'B') this.keyHeld = true;
  };
  private readonly onKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'b' || e.key === 'B') this.keyHeld = false;
  };
  private readonly onPointerMove = (e: PointerEvent) => {
    const dom = this.dom;
    if (!dom) return;
    const rect = dom.getBoundingClientRect();
    this.ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.hasCursor = true;
  };
  private readonly onPointerLeave = () => {
    this.hasCursor = false;
  };

  constructor(terrain: Terrain) {
    this.terrain = terrain;

    this.group = new THREE.Group();
    this.group.name = 'BugSpray';
    this.group.visible = false;

    this.geometry = this.buildFanGeometry();

    this.material = new THREE.MeshBasicMaterial({
      color: 0xc8f0d0,
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    // Laid flat on the ground.
    this.mesh.rotation.x = -Math.PI / 2;
    this.group.add(this.mesh);
  }

  attach(dom: HTMLCanvasElement, camera: THREE.PerspectiveCamera): void {
    this.dom = dom;
    this.camera = camera;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    dom.addEventListener('pointermove', this.onPointerMove);
    dom.addEventListener('pointerleave', this.onPointerLeave);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    const dom = this.dom;
    if (dom) {
      dom.removeEventListener('pointermove', this.onPointerMove);
      dom.removeEventListener('pointerleave', this.onPointerLeave);
    }
    this.geometry.dispose();
    this.material.dispose();
    this.dom = null;
    this.camera = null;
  }

  update(
    dt: number,
    damage: (x: number, z: number, radius: number, dps: number, dt: number) => number,
    scatter: (x: number, z: number, radius: number, force?: number) => void,
  ): void {
    const camera = this.camera;
    if (!camera || !this.keyHeld || !this.hasCursor) {
      this.group.visible = false;
      return;
    }

    // Raycast cursor → terrain mesh.
    this.raycaster.setFromCamera(this.ndc, camera);
    const hits = this.raycaster.intersectObject(this.terrain.mesh, false);
    this.hasHit = hits.length > 0;
    if (!this.hasHit) {
      this.group.visible = false;
      return;
    }

    this.hitPoint.copy(hits[0].point);

    // Spray direction = XZ vector from camera to the hit point (points "away"
    // from where the camera is looking from). Zero-length fallback: +X.
    const dx = this.hitPoint.x - camera.position.x;
    const dz = this.hitPoint.z - camera.position.z;
    const len = Math.hypot(dx, dz);
    if (len > 1e-6) {
      this.dir.set(dx / len, dz / len);
    } else {
      this.dir.set(1, 0);
    }

    const y = this.terrain.getHeight(this.hitPoint.x, this.hitPoint.z) + 0.02;
    this.group.position.set(this.hitPoint.x, y, this.hitPoint.z);
    // Cone geometry is authored pointing along +X in the local XY plane (then
    // rotated flat onto XZ by mesh.rotation.x). Rotate group around Y to align
    // local +X with the XZ direction. In world XZ, heading 0 = +X, increasing
    // toward -Z, so group.rotation.y = -atan2(dirZ, dirX).
    this.group.rotation.y = -Math.atan2(this.dir.y, this.dir.x);
    this.group.visible = true;

    // Damage + scatter at an approximation point: middle of the cone along the
    // spray direction. Scatter shares the same centre but uses a larger radius.
    const midDist = this.length * 0.5;
    const cx = this.hitPoint.x + this.dir.x * midDist;
    const cz = this.hitPoint.z + this.dir.y * midDist;
    damage(cx, cz, this.damageRadius, this.dps, dt);
    scatter(cx, cz, this.scatterRadius, this.scatterForce);
  }

  /**
   * Build a flat fan/sector in the local XY plane, pointing along +X. The tip
   * sits at the origin; the arc is sampled at `segments + 1` points across a
   * full opening angle sized so the arc's chord at distance `length` equals
   * `farWidth`.
   */
  private buildFanGeometry(): THREE.BufferGeometry {
    const geo = new THREE.BufferGeometry();
    const segments = this.segments;
    const length = this.length;
    // Half-opening angle so that at distance `length` the perpendicular extent
    // is ±farWidth/2.
    const halfAngle = Math.atan2(this.farWidth * 0.5, length);

    const vertexCount = segments + 2; // tip + (segments+1) arc verts
    const positions = new Float32Array(vertexCount * 3);
    // Tip at origin.
    positions[0] = 0;
    positions[1] = 0;
    positions[2] = 0;
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const ang = -halfAngle + t * (2 * halfAngle);
      const vi = (i + 1) * 3;
      positions[vi] = Math.cos(ang) * length;
      positions[vi + 1] = Math.sin(ang) * length;
      positions[vi + 2] = 0;
    }

    const indices: number[] = [];
    for (let i = 0; i < segments; i++) {
      indices.push(0, i + 1, i + 2);
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setIndex(indices);
    geo.computeVertexNormals();
    return geo;
  }
}
